// Design-faithful sphere family/type picker (design: sphere-type-filter.jsx —
// SphereValuePicker + SphereValueChip). Selected families/types render as
// badge+glyph chip tokens; "Add family" / "Add type" open a grouped, searchable
// checklist. Adapted to the inspector rail as an inline expandable panel rather
// than a floating 560px popover (which would overflow the narrow rail).
//
// Reuses the canonical schema utils (getEligibleSchemaObjects / getSchemaCategories)
// — this molecule owns presentation only, not type resolution. Families stay
// current (a new type added to a family later matches automatically); types are a
// fixed set grouped under their family.
import React, { useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import { getEligibleSchemaObjects, getSchemaCategories } from '@center-platform/spheres/identity';
import type { OrgAvailableTypeSchema, SchemaSource } from 'types/schema';
import './SphereTypeSelectMolecule.scss';

type Axis = 'family' | 'type';

export type { SchemaSource };

export interface SphereTypeSelectMoleculeHandle {
  /** Closes the open family/type picker panel without changing the applicability mode. */
  closePanel: () => void;
}

/**
 * Contract datum ids for a host whose decomposition covers this picker — the relationship
 * sheet's `<SphereValuePicker>` (`context/sphere-type-filter.jsx:168-320`). The ids belong to
 * the host's contract, not to this molecule, so they are passed in the way
 * `AgentThreadOrganism` takes its rail's: unset everywhere else, and then nothing is stamped.
 */
export interface SphereTypeSelectParity {
  /** The "Choose families" / "Choose types" / "Change" trigger. */
  trigger?: string;
  /** The ✕ on a selected chip. */
  chipRemove?: string;
  /** `Clear (n)` in the open panel's head. */
  clear?: string;
  /** The panel's search box. */
  search?: string;
  /** The ✕ that empties the search box — only rendered while a query is typed. */
  searchClear?: string;
  /** A family row in the FAMILIES axis (it carries the "N types" count). */
  familyOption?: string;
  /** A family's group head in the TYPES axis — the collapse toggle. */
  familyGroup?: string;
  /** A type row under a family group. */
  typeOption?: string;
  /** The panel's `Done`. */
  done?: string;
}

export interface SphereTypeSelectMoleculeProps {
  /** Full schema set to pick families/types from — this molecule computes the scope. */
  schemas: OrgAvailableTypeSchema[];
  selectedFamilies: string[];
  selectedTypes: string[];
  onFamiliesChange: (families: string[]) => void;
  onTypesChange: (types: string[]) => void;
  /**
   * Per-type source classification (system_name → 'platform'|'org'|'approach'),
   * e.g. from `useOrgAvailableTypes`. Authoritative when present. Types render
   * a source tag only when `sources` and/or `approachSources` is supplied —
   * omitting both keeps the picker's prior (untagged) behavior.
   */
  sources?: Record<string, SchemaSource>;
  /**
   * Which approach(es) provided each type. Used to derive 'approach' when
   * `sources` doesn't cover a system_name; everything else defaults to
   * 'platform' (this molecule has no tenant id to distinguish 'org' on its
   * own — that distinction requires the authoritative `sources` map).
   */
  approachSources?: Record<string, string[]>;
  /**
   * Restrict the picker to one axis. 'both' (default) shows family + type — the
   * applicability "Specific types" surface. A single-axis value editor (a `type`
   * or `label` property filter) passes 'type' / 'family' to show only that axis.
   */
  axis?: 'family' | 'type' | 'both';
  /**
   * Axis whose picker panel should be open on first render. Useful when the
   * host already knows which axis the user intends to edit (e.g. the inline
   * filter editor for "Sphere Type" can open the type panel immediately,
   * skipping the "Add type" click). Defaults to `null` (closed).
   */
  defaultOpenAxis?: 'family' | 'type' | null;
  /**
   * Column-width variant (`sphere-type-filter.jsx` `SphereValuePicker`'s
   * `dense` flag) — drops the panel's title/blurb (the row above already
   * says which condition this is) and moves "Clear (N)" from the header row
   * down beside the search input (`uf-identity.clear#2`), tightening padding
   * and font sizes throughout. Defaults to `false`, today's full popover
   * sizing.
   */
  dense?: boolean;
  locale?: string;
  readOnly?: boolean;
  /** See {@link SphereTypeSelectParity}. Unset outside the type composer. */
  parity?: SphereTypeSelectParity;
  /**
   * Seeds the interaction state a reader reaches by clicking — which panel is open, what is
   * typed in its search, which family groups are folded. A story or a test declares the
   * reading it is showing instead of driving clicks to get there; nothing else passes it,
   * and the picker then opens closed and empty as before.
   */
  initialOpenAxis?: Axis;
  initialQuery?: string;
  /** Family `schema_id`s folded away in the TYPES axis. */
  initialCollapsedFamilies?: string[];
  className?: string;
  /**
   * Parity instrumentation only (`uf-identity.input`/`uf-identity.input#2` — the
   * prototype's `<SphereValuePicker>` and `<SphereValuePicker>#2` each carry their
   * own search input). Omitted everywhere except the dedicated parity story.
   */
  searchInputDatumId?: string;
  /**
   * Parity instrumentation only — the prototype's `uf-identity` artboard mounts
   * `<SphereValuePicker>` twice side by side, so the second instance's elements
   * carry `#N`-suffixed datum ids (`uf-identity.clear#2`, `uf-identity.button#4`,
   * …) for the same affordances the first instance already covers. Every key
   * defaults to the unsuffixed id; only the dedicated side-by-side story
   * overrides them.
   */
  datumIds?: Partial<{
    clear: string;
    /** The `dense` panel's own inline "Clear N" beside the search input — `uf-identity.clear#2`. */
    clearDense: string;
    queryClear: string;
    familyOption: string;
    familyCheckbox: string;
    familyGlyph: string;
    groupHeader: string;
    groupGlyph: string;
    leafOption: string;
    leafCheckbox: string;
    leafGlyph: string;
    sourcePill: string;
    done: string;
  }>;
  /**
   * When supplied, renders a "Done" affordance in the open panel's footer
   * (`uf-identity.done`) — the prototype's explicit close-and-confirm action
   * for a popover-hosted picker. Omitted by the inline inspector-rail usage,
   * whose panel closes via the same "Add family"/"Add type" trigger.
   */
  onDone?: () => void;
  /**
   * Renders the checklist PANEL alone — no empty-state message, no "Choose families"/
   * "Choose types" axis triggers, no outer selected-chips row and no panel-internal
   * chips row. For a host that already owns those affordances (the prototype's
   * `SphereValuePicker`, whose parent list picks the axis and whose own row carries the
   * chips) and would otherwise render each of them twice. The panel opens on
   * `initialOpenAxis`/`defaultOpenAxis` when given, else on this picker's single axis
   * (`family` when `axis` is `'both'`) — a panel-only mount with nothing open would
   * render nothing at all. Defaults to `false`: today's full picker, unchanged.
   */
  panelOnly?: boolean;
}

/** Resolve a type's source tag. Returns undefined (no tag) when neither prop is supplied. */
function resolveTypeSource(
  systemName: string,
  sources: Record<string, SchemaSource> | undefined,
  approachSources: Record<string, string[]> | undefined
): SchemaSource | undefined {
  if (!sources && !approachSources) return undefined;
  if (sources?.[systemName]) return sources[systemName];
  return approachSources?.[systemName]?.length ? 'approach' : 'platform';
}

/** Walk `parent_schema_id` up from `schema`; true if `ancestorSchemaId` is on the path (inclusive). */
function isDescendantOfOrSelf(
  schema: OrgAvailableTypeSchema,
  ancestorSchemaId: string,
  byId: Map<string, OrgAvailableTypeSchema>
): boolean {
  let current: OrgAvailableTypeSchema | undefined = schema;
  const visited = new Set<string>();
  while (current) {
    if (current.schema_id === ancestorSchemaId) return true;
    if (visited.has(current.schema_id)) return false;
    visited.add(current.schema_id);
    current = current.parent_schema_id ? byId.get(current.parent_schema_id) : undefined;
  }
  return false;
}

const SphereTypeSelectMolecule = React.forwardRef<
  SphereTypeSelectMoleculeHandle,
  SphereTypeSelectMoleculeProps
>(({
  schemas,
  selectedFamilies,
  selectedTypes,
  onFamiliesChange,
  onTypesChange,
  sources,
  approachSources,
  axis = 'both',
  defaultOpenAxis = null,
  dense = false,
  locale = 'en-US',
  readOnly = false,
  parity,
  initialOpenAxis,
  initialQuery = '',
  initialCollapsedFamilies,
  className = '',
  searchInputDatumId,
  datumIds,
  onDone,
  panelOnly = false,
}, ref) => {
  const showFamily = axis !== 'type';
  const showType = axis !== 'family';
  const { t } = useTranslation();
  /**
   * The datum id stamped on an element. A host that brings its own contract (`parity`) stamps
   * only the ids it names — this picker's default `uf-identity` ids belong to the filter
   * surfaces' contract, not to that host's. Without `parity`, `datumIds` overrides the default.
   */
  const dd = useCallback(
    (
      key: keyof NonNullable<SphereTypeSelectMoleculeProps['datumIds']>,
      fallback: string,
      parityId?: string
    ): string | undefined => (parity ? parityId : datumIds?.[key] ?? fallback),
    [datumIds, parity]
  );
  const [openAxis, setOpenAxis] = useState<Axis | null>(
    initialOpenAxis ?? defaultOpenAxis ?? (panelOnly ? (axis === 'type' ? 'type' : 'family') : null)
  );
  const [query, setQuery] = useState(initialQuery);
  // Type axis only — which family groups are folded away. A query in progress always
  // expands every group (matching results shouldn't hide), so this is read through `!q`,
  // never cleared on its own.
  const [collapsedFamilies, setCollapsedFamilies] = useState<Record<string, boolean>>(() =>
    Object.fromEntries((initialCollapsedFamilies ?? []).map((id) => [id, true]))
  );

  // `datumId` is only supplied by the panel checklist's leaf row (the
  // prototype's own `uf-identity.pill` datum) — the "selected chips" row
  // above the panel reuses this same function for its badge, which the
  // contract doesn't attribute to that datum, so it renders untagged.
  const sourceTag = useCallback(
    (systemName: string, datumId?: string) => {
      const source = resolveTypeSource(systemName, sources, approachSources);
      if (!source) return null;
      return (
        <span
          className={`sphere-type-select-molecule__source-tag sphere-type-select-molecule__source-tag--${source}`}
          data-testid={`type-select-source-${systemName}`}
          data-datum={datumId}
          data-kind={datumId ? 'pill' : undefined}
        >
          {t(`sphereTypeSelect.sourceBadge.${source}`)}
        </span>
      );
    },
    [sources, approachSources, t]
  );

  const localName = useCallback(
    (s: OrgAvailableTypeSchema) => s.schema_name_translations?.[locale] ?? s.name,
    [locale]
  );

  const byId = useMemo(() => new Map(schemas.map((s) => [s.schema_id, s])), [schemas]);
  const items = useMemo(() => getEligibleSchemaObjects(schemas), [schemas]);
  const families = useMemo(
    () =>
      getSchemaCategories(items, schemas, 1, locale).filter(
        // The abstract system root ('BaseSchema') is internal scaffolding, not a
        // user-facing family. Real families sit below it, so excluding it never
        // hides a type.
        (f) => f.system_name !== 'center_baseschema' && f.name !== 'BaseSchema'
      ),
    [items, schemas, locale]
  );

  // system_name → full schema, so chips can render the schema's own glyph.
  const familyBySn = useMemo(
    () => new Map(families.map((f) => [f.system_name, f])),
    [families]
  );
  const typeBySn = useMemo(
    () => new Map(items.map((i) => [i.schema.system_name, i.schema])),
    [items]
  );

  // Family system_name → display name, and type system_name → { name, familyName }.
  const familyName = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of families) m.set(f.system_name, localName(f));
    return m;
  }, [families, localName]);

  const typeMeta = useMemo(() => {
    const m = new Map<string, { name: string; familyName: string }>();
    for (const item of items) {
      const s = item.schema;
      const fam = families.find((f) => isDescendantOfOrSelf(s, f.schema_id, byId));
      m.set(s.system_name, { name: localName(s), familyName: fam ? localName(fam) : '' });
    }
    return m;
  }, [items, families, byId, localName]);

  // Types grouped under their family (types sorted alphabetically within each),
  // for the "type" checklist.
  const typesByFamily = useMemo(
    () =>
      families.map((f) => ({
        family: f,
        types: items
          .filter((item) => isDescendantOfOrSelf(item.schema, f.schema_id, byId))
          .map((item) => item.schema)
          .sort((a, b) => localName(a).localeCompare(localName(b))),
      })),
    [families, items, byId, localName]
  );

  const q = query.trim().toLowerCase();
  const isFamilyAxis = openAxis === 'family';

  // Footer counter: families carry an approximate descendant-sphere count; types are a plain tally.
  let panelCounterLabel: string;
  if (isFamilyAxis) {
    const sphereCount = selectedFamilies.reduce(
      (sum, fn) =>
        sum +
        items.filter((item) =>
          isDescendantOfOrSelf(item.schema, familyBySn.get(fn)?.schema_id ?? '', byId)
        ).length,
      0
    );
    panelCounterLabel = t('sphereTypeSelect.familyCounter', {
      count: selectedFamilies.length,
      spheres: sphereCount,
    });
  } else {
    panelCounterLabel = t('sphereTypeSelect.typeCount', {
      count: selectedTypes.length,
    });
  }
  const selected = isFamilyAxis ? selectedFamilies : selectedTypes;
  const emitSelected = isFamilyAxis ? onFamiliesChange : onTypesChange;

  const toggle = useCallback(
    (systemName: string) => {
      const next = selected.includes(systemName)
        ? selected.filter((n) => n !== systemName)
        : [...selected, systemName];
      emitSelected(next);
    },
    [selected, emitSelected]
  );

  const removeFamily = (name: string) => onFamiliesChange(selectedFamilies.filter((n) => n !== name));
  const removeType = (name: string) => onTypesChange(selectedTypes.filter((n) => n !== name));

  const hasSelection =
    (showFamily && selectedFamilies.length > 0) || (showType && selectedTypes.length > 0);

  const openPanel = (axis: Axis) => {
    setOpenAxis((prev) => (prev === axis ? null : axis));
    setQuery('');
  };

  useImperativeHandle(ref, () => ({
    closePanel: () => setOpenAxis(null),
  }));

  /**
   * The trigger says what pressing it does. With nothing chosen on that axis it invites the
   * first choice ("Choose families"); with something chosen it offers to revise ("Change") —
   * the wording the design uses, and the only reading under which the label is ever true.
   */
  const triggerLabel = (forAxis: Axis) =>
    (forAxis === 'family' ? selectedFamilies.length : selectedTypes.length) > 0
      ? t('sphereTypeSelect.change')
      : t(
          `sphereTypeSelect.${
            forAxis === 'family' ? 'addFamily' : 'addType'
          }`
        );

  const axisHasSelection = isFamilyAxis ? selectedFamilies.length > 0 : selectedTypes.length > 0;

  // What the open checklist shows for the current query. A query in progress
  // always expands every group (matching results shouldn't hide).
  const matchesQuery = (name: string) => !q || name.toLowerCase().includes(q);
  const visibleFamilies = families.filter((f) => matchesQuery(localName(f)));
  const visibleTypeGroups = typesByFamily
    .map(({ family, types }) => ({ family, types: types.filter((s) => matchesQuery(localName(s))) }))
    .filter((group) => group.types.length > 0);
  const nothingMatches = isFamilyAxis ? visibleFamilies.length === 0 : visibleTypeGroups.length === 0;

  // One checklist row. The native checkbox is the control (keyboard, assistive
  // tech); the label around it carries the row's styling and its parity datum.
  const renderOption = (row: {
    key: string;
    systemName: string;
    on: boolean;
    testId: string;
    datum: string | undefined;
    checkboxDatum: string | undefined;
    glyphDatum: string | undefined;
    container: string | undefined;
    glyph: string | null | undefined;
    glyphColor: React.ComponentProps<typeof GlyphAtom>['glyphColor'] | null;
    name: string;
    // Family and type rows share this markup but the prototype treats them as
    // two distinct controls (sphere-type-filter.jsx:248 vs :286 — different
    // padding/gap) — the modifier class is how the shared CSS class expresses that.
    variant: 'family' | 'type';
    children: React.ReactNode;
  }) => (
    <label
      key={row.key}
      className={`sphere-type-select-molecule__option sphere-type-select-molecule__option--${row.variant}${row.on ? ' sphere-type-select-molecule__option--on' : ''}`}
      data-testid={row.testId}
      data-container={row.container}
      data-datum={row.datum}
      data-kind={row.datum ? 'button' : undefined}
    >
      <input
        type="checkbox"
        className="sphere-type-select-molecule__option-input"
        checked={row.on}
        onChange={() => toggle(row.systemName)}
      />
      <span
        className="sphere-type-select-molecule__check"
        aria-hidden="true"
        data-datum={row.checkboxDatum}
        data-kind={row.checkboxDatum ? 'checkbox' : undefined}
      >
        {row.on && <i className="pi pi-check" />}
      </span>
      <span
        className="sphere-type-select-molecule__option-glyph"
        aria-hidden="true"
        data-datum={row.glyphDatum}
        data-kind={row.glyphDatum ? 'glyph' : undefined}
      >
        <GlyphAtom glyph={row.glyph ?? 'default-icon'} glyphColor={row.glyphColor ?? undefined} fallbackText={row.name} />
      </span>
      {row.children}
    </label>
  );

  const renderFamilyOption = (f: (typeof families)[number]) => {
    const typeCount = items.filter((item) => isDescendantOfOrSelf(item.schema, f.schema_id, byId)).length;
    return renderOption({
      key: f.schema_id,
      systemName: f.system_name,
      on: selectedFamilies.includes(f.system_name),
      testId: `type-select-option-family-${f.system_name}`,
      datum: dd('familyOption', 'uf-identity.types', parity?.familyOption),
      checkboxDatum: dd('familyCheckbox', 'uf-identity.checkbox'),
      glyphDatum: dd('familyGlyph', 'uf-identity.glyph'),
      container: parity ? 'each(families)' : undefined,
      glyph: f.glyph,
      glyphColor: f.glyph_color,
      name: localName(f),
      variant: 'family',
      children: (
        <>
          <span className="sphere-type-select-molecule__option-content">
            <span className="sphere-type-select-molecule__option-label">{localName(f)}</span>
            {f.description && (
              <span className="sphere-type-select-molecule__option-description">{f.description}</span>
            )}
          </span>
          {typeCount > 0 && (
            <span className="sphere-type-select-molecule__option-meta">
              {t('sphereTypeSelect.typeCount', { count: typeCount })}
            </span>
          )}
        </>
      ),
    });
  };

  const renderTypeOption = (tSchema: OrgAvailableTypeSchema) =>
    renderOption({
      key: tSchema.schema_id,
      systemName: tSchema.system_name,
      on: selectedTypes.includes(tSchema.system_name),
      testId: `type-select-option-type-${tSchema.system_name}`,
      datum: dd('leafOption', 'uf-identity.button#3', parity?.typeOption),
      checkboxDatum: dd('leafCheckbox', 'uf-identity.checkbox#2'),
      glyphDatum: dd('leafGlyph', 'uf-identity.glyph#3'),
      container: parity ? 'each(families) > each(types)' : undefined,
      glyph: tSchema.glyph,
      glyphColor: tSchema.glyph_color,
      name: localName(tSchema),
      variant: 'type',
      children: (
        <>
          <span className="sphere-type-select-molecule__option-label">{localName(tSchema)}</span>
          {sourceTag(tSchema.system_name, dd('sourcePill', 'uf-identity.pill'))}
        </>
      ),
    });

  // A family group of the type checklist: a collapsible header, then its rows.
  // Collapsing only applies to the at-rest browse — a query in progress always
  // shows results, matching the prototype's `isCol` guard.
  const renderTypeGroup = ({ family, types }: (typeof visibleTypeGroups)[number]) => {
    const isCollapsed = !!collapsedFamilies[family.schema_id] && !q;
    const selectedInFamily = selectedTypes.filter((sn) => {
      const schema = typeBySn.get(sn);
      return !!schema && isDescendantOfOrSelf(schema, family.schema_id, byId);
    }).length;
    const groupDatum = dd('groupHeader', 'uf-identity.button#2', parity?.familyGroup);
    const groupGlyphDatum = dd('groupGlyph', 'uf-identity.glyph#2');
    return (
      <div
        key={family.schema_id}
        className="sphere-type-select-molecule__group"
        data-container={parity ? 'each(families)' : 'each(matchTypes)'}
      >
        <button
          type="button"
          className="sphere-type-select-molecule__group-label"
          onClick={() =>
            setCollapsedFamilies((prev) => ({
              ...prev,
              [family.schema_id]: !prev[family.schema_id],
            }))
          }
          aria-expanded={!isCollapsed}
          aria-label={t(`sphereTypeSelect.${isCollapsed ? 'expandFamily' : 'collapseFamily'}`, {
            name: localName(family),
          })}
          disabled={!!q}
          data-testid={`type-select-group-toggle-${family.system_name}`}
          data-datum={groupDatum}
          data-kind={groupDatum ? 'button' : undefined}
        >
          <span
            className="sphere-type-select-molecule__group-glyph"
            aria-hidden="true"
            data-datum={groupGlyphDatum}
            data-kind={groupGlyphDatum ? 'glyph' : undefined}
          >
            <GlyphAtom
              glyph={family.glyph ?? 'default-icon'}
              glyphColor={family.glyph_color ?? undefined}
              fallbackText={localName(family)}
            />
          </span>
          <span className="sphere-type-select-molecule__group-label-text">{localName(family)}</span>
          {selectedInFamily > 0 && (
            <span className="sphere-type-select-molecule__group-count">{selectedInFamily}</span>
          )}
          {!q && (
            <i
              className={`pi ${isCollapsed ? 'pi-chevron-down' : 'pi-chevron-up'} sphere-type-select-molecule__group-chevron`}
              aria-hidden="true"
            />
          )}
        </button>
        {!isCollapsed && types.map(renderTypeOption)}
      </div>
    );
  };

  // "Add family" / "Add type": the empty state's pills (aria-pressed) and the
  // segmented triggers beside the chips (aria-expanded) share one shape; each
  // axis renders only when the picker shows it.
  const renderAxisTriggers = (variant: 'empty' | 'add') => {
    const base = variant === 'empty' ? 'sphere-type-select-molecule__empty-add' : 'sphere-type-select-molecule__add';
    const trigger = (axisToOpen: Axis) => {
      const active = openAxis === axisToOpen;
      const isFamilyTrigger = axisToOpen === 'family';
      const activeClass = active ? `${base}--active` : '';
      // Only the families trigger is a node in a host contract; the types trigger is not.
      const triggerDatum = isFamilyTrigger ? parity?.trigger : undefined;
      return (
        <button
          type="button"
          className={`${base} ${activeClass}`.trim()}
          onClick={() => openPanel(axisToOpen)}
          aria-pressed={variant === 'empty' ? active : undefined}
          aria-expanded={variant === 'add' ? active : undefined}
          data-testid={`type-select-add-${axisToOpen}${variant === 'empty' ? '-empty' : ''}`}
          data-datum={triggerDatum}
          data-kind={triggerDatum ? 'button' : undefined}
        >
          <i className={isFamilyTrigger ? 'pi pi-sitemap' : 'pi pi-tag'} aria-hidden="true" />
          {triggerLabel(axisToOpen)}
        </button>
      );
    };
    return (
      <>
        {showFamily && trigger('family')}
        {showType && trigger('type')}
      </>
    );
  };

  // A selected family/type as a badge + glyph + label chip token.
  const renderChip = (kind: Axis, sn: string) => {
    const schema = kind === 'family' ? familyBySn.get(sn) : typeBySn.get(sn);
    const name = (kind === 'family' ? familyName.get(sn) : typeMeta.get(sn)?.name) ?? sn;
    const remove = kind === 'family' ? removeFamily : removeType;
    const removeDatum = kind === 'family' ? parity?.chipRemove : undefined;
    return (
      <span
        key={`${kind === 'family' ? 'fam' : 'type'}-${sn}`}
        className="sphere-type-select-molecule__chip"
        data-container="each(chosen)"
        data-testid={`type-chip-${kind}-${sn}`}
      >
        <span className={`sphere-type-select-molecule__chip-badge sphere-type-select-molecule__chip-badge--${kind}`}>
          {t(
            kind === 'family'
              ? 'sphereTypeSelect.familyBadge'
              : 'sphereTypeSelect.typeBadge'
          )}
        </span>
        <span className="sphere-type-select-molecule__chip-glyph" aria-hidden="true">
          <GlyphAtom glyph={schema?.glyph ?? 'default-icon'} glyphColor={schema?.glyph_color ?? undefined} fallbackText={name} />
        </span>
        <span className="sphere-type-select-molecule__chip-label">{name}</span>
        {kind === 'type' && sourceTag(sn)}
        {!readOnly && (
          <button
            type="button"
            className="sphere-type-select-molecule__chip-remove"
            aria-label={t('sphereTypeSelect.remove', { name })}
            onClick={() => remove(sn)}
            data-datum={removeDatum}
            data-kind={removeDatum ? 'button' : undefined}
          >
            <i className="pi pi-times" aria-hidden="true" />
          </button>
        )}
      </span>
    );
  };

  const renderChips = () => (
    <div className="sphere-type-select-molecule__chips">
      {showFamily && selectedFamilies.map((sn) => renderChip('family', sn))}
      {showType && selectedTypes.map((sn) => renderChip('type', sn))}
    </div>
  );

  // The open axis's checklist panel: what is selected on it, its title + hint,
  // search, the rows, and the counter/Done footer.
  const renderPanel = () => {
    const copy = isFamilyAxis
      ? {
          title: t('sphereTypeSelect.chooseFamilies'),
          hint: t('sphereTypeSelect.familiesHint'),
          search: t('sphereTypeSelect.searchFamilies'),
        }
      : {
          title: t('sphereTypeSelect.chooseTypes'),
          hint: t('sphereTypeSelect.typesHint'),
          search: t('sphereTypeSelect.searchTypes'),
        };
    const renderPanelChip = (sn: string) => {
      const name = (isFamilyAxis ? familyName.get(sn) : typeMeta.get(sn)?.name) ?? sn;
      const remove = isFamilyAxis ? removeFamily : removeType;
      return (
        <span key={`selected-${sn}`} className="sphere-type-select-molecule__panel-chip">
          <span className="sphere-type-select-molecule__panel-chip-label">{name}</span>
          {!readOnly && (
            <button
              type="button"
              className="sphere-type-select-molecule__panel-chip-remove"
              onClick={() => remove(sn)}
              aria-label={t('sphereTypeSelect.remove', { name })}
            >
              <i className="pi pi-times" aria-hidden="true" />
            </button>
          )}
        </span>
      );
    };
    const clearDatum = dd('clear', 'uf-identity.clear', parity?.clear);
    const clearDenseDatum = dd('clearDense', 'uf-identity.clear#2');
    const searchDatum = parity ? parity.search : searchInputDatumId;
    const searchClearDatum = dd('queryClear', 'uf-identity.button', parity?.searchClear);
    const doneDatum = dd('done', 'uf-identity.done', parity?.done);
    return (
      <div
        className={`sphere-type-select-molecule__panel${dense ? ' sphere-type-select-molecule__panel--dense' : ''}`}
        data-testid="type-select-panel"
      >
        {/* Selected items shown in the panel (if any) — a `panelOnly` host supplies its
            own chips, so this second row would repeat them. */}
        {axisHasSelection && !panelOnly && (
          <div className="sphere-type-select-molecule__panel-selected">{selected.map(renderPanelChip)}</div>
        )}

        {/* Section header with help text — dense drops both: the row above the
            panel already says which condition this is, and at column width
            the explanation costs more than the options it explains
            (sphere-type-filter.jsx:191-217). */}
        {!dense && (
          <div className="sphere-type-select-molecule__panel-header">
            <div className="sphere-type-select-molecule__panel-header-row">
              <div className="sphere-type-select-molecule__panel-header-title">{copy.title}</div>
              {selected.length > 0 && (
                <button
                  type="button"
                  className="sphere-type-select-molecule__panel-clear"
                  onClick={() => emitSelected([])}
                  data-testid="type-select-clear"
                  data-datum={clearDatum}
                  data-kind={clearDatum ? 'button' : undefined}
                >
                  <i className="pi pi-times" aria-hidden="true" />
                  {t('sphereTypeSelect.clear', { count: selected.length })}
                </button>
              )}
            </div>
            <div className="sphere-type-select-molecule__panel-header-hint">{copy.hint}</div>
          </div>
        )}

        <div className="sphere-type-select-molecule__panel-search">
          <i className="pi pi-search" aria-hidden="true" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-datum={searchDatum}
            data-kind={searchDatum ? 'input' : undefined}
            placeholder={copy.search}
            aria-label={copy.search}
          />
          {/* Dense moves "Clear (N)" down beside the search box instead of the
              (now-absent) header row — sphere-type-filter.jsx:229-231. */}
          {dense && selected.length > 0 && (
            <button
              type="button"
              className="sphere-type-select-molecule__panel-clear sphere-type-select-molecule__panel-clear--dense"
              onClick={() => emitSelected([])}
              data-testid="type-select-clear-dense"
              data-datum={clearDenseDatum}
              data-kind={clearDenseDatum ? 'button' : undefined}
              title={t('sphereTypeSelect.clear', { count: selected.length })}
            >
              {t('sphereTypeSelect.clear', { count: selected.length })}
            </button>
          )}
          {query && !dense && (
            <button
              type="button"
              className="sphere-type-select-molecule__panel-search-clear"
              onClick={() => setQuery('')}
              aria-label={t('sphereTypeSelect.clearSearch')}
              data-testid="type-select-clear-search"
              data-datum={searchClearDatum}
              data-kind={searchClearDatum ? 'button' : undefined}
            >
              <i className="pi pi-times" aria-hidden="true" />
            </button>
          )}
        </div>

        <div
          className="sphere-type-select-molecule__panel-body"
          data-container={parity ? undefined : 'each(FAMS)'}
        >
          {isFamilyAxis ? visibleFamilies.map(renderFamilyOption) : visibleTypeGroups.map(renderTypeGroup)}
          {nothingMatches && (
            <div className="sphere-type-select-molecule__panel-empty">
              {t('sphereTypeSelect.noResults')}
            </div>
          )}
        </div>

        <div className="sphere-type-select-molecule__panel-footer">
          <span className="sphere-type-select-molecule__panel-counter">
            {axisHasSelection
              ? panelCounterLabel
              : t('sphereTypeSelect.nothingSelected')}
          </span>
          {onDone && (
            <>
              <span className="sphere-type-select-molecule__panel-footer-spacer" />
              <button
                type="button"
                className="sphere-type-select-molecule__done"
                onClick={() => {
                  setOpenAxis(null);
                  onDone();
                }}
                data-testid="type-select-done"
                data-datum={doneDatum}
                data-kind={doneDatum ? 'button' : undefined}
              >
                <i className="pi pi-check" aria-hidden="true" />
                {t('sphereTypeSelect.done')}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`sphere-type-select-molecule ${className}`} data-testid="sphere-type-select">
      {/* Empty state container (shown when no families/types selected and the panel is closed) */}
      {!panelOnly && !hasSelection && !openAxis && !readOnly && (
        <div className="sphere-type-select-molecule__empty-container">
          <span className="sphere-type-select-molecule__empty-message">
            {t('sphereTypeSelect.empty')}
          </span>
          <div className="sphere-type-select-molecule__empty-actions">{renderAxisTriggers('empty')}</div>
        </div>
      )}

      {/* Selected chips (families first, then types) */}
      {!panelOnly && hasSelection && renderChips()}

      {/* Add family / Add type triggers */}
      {!panelOnly && hasSelection && !readOnly && (
        <div className="sphere-type-select-molecule__actions">{renderAxisTriggers('add')}</div>
      )}

      {/* Inline grouped checklist (opens under the triggers) */}
      {openAxis && !readOnly && renderPanel()}
    </div>
  );
});

SphereTypeSelectMolecule.displayName = 'SphereTypeSelectMolecule';

export default SphereTypeSelectMolecule;
